import * as React from "react"
import { router } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { Pencil, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { CardForm } from "./CardForm"
import { CardList } from "./CardList"
import { ConfirmDialog } from "./ConfirmDialog"
import type { ContentAreaGroup, DeckDetail, Flashcard } from "@/types/flashcards"

/**
 * Card list with add / edit / delete for a deck the viewer can manage.
 * `basePath` is the deck's URL ("/decks/1" or "/admin/decks/1").
 */
export function DeckEditor({
  deck,
  basePath,
  contentAreaGroups,
}: {
  deck: DeckDetail
  basePath: string
  contentAreaGroups: ContentAreaGroup[]
}) {
  const intl = useIntl()
  const [editing, setEditing] = React.useState<Flashcard | null>(null)
  const [deleting, setDeleting] = React.useState<Flashcard | null>(null)
  const [processing, setProcessing] = React.useState(false)

  const confirmDelete = () => {
    if (!deleting) return
    router.delete(`${basePath}/flashcards/${deleting.id}`, {
      preserveScroll: true,
      onStart: () => setProcessing(true),
      onFinish: () => {
        setProcessing(false)
        setDeleting(null)
      },
    })
  }

  return (
    <>
      <section className="mt-8">
        <h2>{intl.formatMessage({ id: "flashcards.add_title" })}</h2>
        <div className="callout mt-4">
          <CardForm
            idPrefix="new-card"
            action={`${basePath}/flashcards`}
            method="post"
            contentAreaGroups={contentAreaGroups}
            submitLabel={intl.formatMessage({ id: "flashcards.add_card" })}
          />
        </div>
      </section>

      <section className="mt-10">
        <h2>{intl.formatMessage({ id: "flashcards.cards" })}</h2>
        {deck.cards.length === 0 ? (
          <p className="mt-2">{intl.formatMessage({ id: "flashcards.no_cards_yet" })}</p>
        ) : (
          <CardList
            cards={deck.cards}
            actions={(card) => (
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={intl.formatMessage({ id: "flashcards.edit_card" })}
                  onClick={() => setEditing(card)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={intl.formatMessage({ id: "flashcards.delete_card" })}
                  onClick={() => setDeleting(card)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          />
        )}
      </section>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>{intl.formatMessage({ id: "flashcards.edit_card" })}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="mt-4">
              <CardForm
                key={editing.id}
                idPrefix="edit-card"
                action={`${basePath}/flashcards/${editing.id}`}
                method="patch"
                card={editing}
                contentAreaGroups={contentAreaGroups}
                submitLabel={intl.formatMessage({ id: "flashcards.save_card" })}
                onSuccess={() => setEditing(null)}
                onCancel={() => setEditing(null)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={intl.formatMessage({ id: "flashcards.delete_card_title" })}
        description={intl.formatMessage({ id: "flashcards.delete_card_body" })}
        confirmLabel={intl.formatMessage({ id: "flashcards.delete_card" })}
        onConfirm={confirmDelete}
        processing={processing}
      />
    </>
  )
}

